import { cn } from "@repo/utils/cn";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "../AiDisclaimer";
import { Button, ButtonProps } from "../Button";
import { Link } from "../Link";

interface ResolvedAsset {
  alt: string;
  src: string;
  focus?: string;
  aspectRatio?: string;
  width?: number;
  height?: number;
}

interface FeatureItem {
  icon?: ResolvedAsset;
  text?: string;
  _uid: string;
}

export interface LinedListItemDetailedProps {
  image?: ResolvedAsset;
  title?: string;
  subTitle?: string;
  price?: string;
  priceTitle?: string;
  button?: ButtonProps[];
  features?: FeatureItem[];
  _uid: string;
  className?: string;
  onButtonClick?: () => void;
}

export function LinedListItemDetailed({
  image,
  title,
  subTitle,
  price,
  priceTitle,
  button,
  features = [],
  className,
  onButtonClick,
}: LinedListItemDetailedProps) {
  const hasPrice = typeof price === "string" ? price.trim().length > 0 : false;

  const mobileContent = (
    <div className="uipm:flex uipm:gap-[var(--container-margin)] uipm:pb-[var(--container-card-padding)] uipm:pt-[var(--container-card-medium-padding)] uipm:px-0 uipm:w-full">
      {image && (
        <div className="uipm:relative uipm:rounded-[var(--radius-radius-xs)] uipm:w-[90px] uipm:h-[90px] uipm:shrink-0 uipm:overflow-hidden">
          <div className="uipm:absolute uipm:inset-0 uipm:transition-transform uipm:duration-300 uipm:ease-out uipm:group-hover:scale-110">
            <Image
              alt={parseAiAlt(image.alt).cleanAlt}
              className="uipm:absolute uipm:inset-0 uipm:max-w-none uipm:object-center uipm:object-cover uipm:pointer-events-none uipm:rounded-[var(--radius-radius-xs)] uipm:size-full"
              src={image.src || ""}
              fill
              sizes="120px"
            />
          </div>
          {/* Compact: a 90px thumbnail. Outside the scaling wrapper so it does not zoom. */}
          <AiDisclaimerForAlt alt={image.alt} src={image.src} size="compact" />
        </div>
      )}

      <div className="uipm:flex-1 uipm:flex uipm:flex-col">
        {hasPrice && (
          <p className="uipm:body-medium uipm:text-[var(--typography-colors-text-faded-dark)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:text-(length:--typography-sizes-body-medium) uipm:text-nowrap uipm:whitespace-pre">
            <span>{priceTitle ? `${priceTitle} ` : "from "}</span>
            <span className="uipm:font-bold">{price}</span>
          </p>
        )}

        <h3
          className="uipm:text-[var(--typography-sizes-body-large)] uipm:leading-[var(--typography-line-heights-body-large)] uipm:font-bold uipm:mb-2"
          style={{ color: "var(--typography-colors-text-heading)" }}
        >
          {title}
        </h3>

        <div className="uipm:flex uipm:flex-wrap uipm:gap-4 uipm:items-start">
          {features.map((feature) => (
            <div
              key={feature._uid}
              className="uipm:flex uipm:gap-1.5 uipm:items-center"
            >
              {feature.icon && (
                <div className="uipm:size-4">
                  <Image
                    alt=""
                    className="uipm:block uipm:max-w-none uipm:size-full"
                    src={feature.icon.src || ""}
                    width={16}
                    height={16}
                  />
                </div>
              )}
              <p className="uipm:body-medium uipm:text-[var(--typography-colors-text-faded)] uipm:text-nowrap uipm:whitespace-pre">
                {feature.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn("uipm:relative uipm:w-full", className)}>
      {/* Mobile View - Entire card is clickable */}
      {button && button[0]?.href ? (
        <Link
          href={button[0].href}
          target={button[0].target}
          noFollow={button[0].noFollow}
          onClick={onButtonClick}
          className="uipm:group uipm:block uipm:md:hidden uipm:cursor-pointer uipm:no-underline"
        >
          {mobileContent}
        </Link>
      ) : (
        <div className="uipm:md:hidden">
          {mobileContent}
          {/* A Navattic button has no href to wrap the card in, so it needs a real button here. */}
          {button?.[0]?.navatticUrl && (
            <Button
              {...button[0]}
              onClick={(e) => {
                onButtonClick?.();
                button[0]?.onClick?.(e);
              }}
              size="small"
              className="uipm:w-full uipm:mb-[var(--container-card-padding)] uipm:border-2 uipm:border-[var(--surface-surface-accent)] uipm:rounded-[var(--radius-radius-sm)]"
            />
          )}
        </div>
      )}

      <div className="uipm:group uipm:hidden uipm:md:flex uipm:items-center uipm:gap-[var(--padding-padding-default)] uipm:pb-[var(--container-margin)] uipm:pt-[var(--container-margin)] uipm:px-0 uipm:w-full uipm:cursor-pointer">
        {image && (
          <div className="uipm:relative uipm:rounded-[var(--radius-radius-xs)] uipm:w-28 uipm:h-28 uipm:shrink-0 uipm:overflow-hidden">
            <div className="uipm:absolute uipm:inset-0 uipm:transition-transform uipm:duration-300 uipm:ease-out uipm:group-hover:scale-110">
              <Image
                alt={parseAiAlt(image.alt).cleanAlt}
                className="uipm:absolute uipm:inset-0 uipm:max-w-none uipm:object-center uipm:object-cover uipm:pointer-events-none uipm:rounded-[var(--radius-radius-xs)] uipm:size-full"
                src={image.src || ""}
                fill
                sizes="150px"
              />
            </div>
            {/* Compact: a 112px thumbnail. Outside the scaling wrapper so it does not zoom. */}
            <AiDisclaimerForAlt
              alt={image.alt}
              src={image.src}
              size="compact"
            />
          </div>
        )}

        <div className="uipm:flex-1 uipm:flex uipm:flex-col uipm:gap-[var(--padding-padding-xs)] uipm:min-w-0">
          <div className="uipm:flex uipm:flex-col uipm:items-start">
            <h3
              className="uipm:text-[var(--typography-sizes-body-large)] uipm:leading-[var(--typography-line-heights-body-large)] uipm:font-bold"
              style={{ color: "var(--typography-colors-text-heading)" }}
            >
              {title}
            </h3>
            <p className="uipm:body-medium uipm:text-[var(--typography-colors-text-faded)]">
              {subTitle}
            </p>
          </div>

          <div className="uipm:flex uipm:flex-wrap uipm:gap-4 uipm:items-start">
            {features.map((feature) => (
              <div
                key={feature._uid}
                className="uipm:flex uipm:gap-1.5 uipm:items-center"
              >
                {feature.icon && (
                  <div className="uipm:size-4">
                    <Image
                      alt=""
                      className="uipm:block uipm:max-w-none uipm:size-full"
                      src={feature.icon.src || ""}
                      width={16}
                      height={16}
                    />
                  </div>
                )}
                <p className="uipm:body-medium uipm:text-[var(--typography-colors-text-faded)] uipm:text-nowrap uipm:whitespace-pre">
                  {feature.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="uipm:flex uipm:items-center uipm:gap-[var(--padding-padding-default)] uipm:shrink-0">
          {hasPrice && (
            <p className="uipm:body-medium uipm:text-[var(--typography-colors-text-faded-dark)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:text-(length:--typography-sizes-body-medium) uipm:text-nowrap uipm:whitespace-pre">
              <span>{priceTitle ? `${priceTitle} ` : "from "}</span>
              <span className="uipm:font-bold">{price}</span>
            </p>
          )}
          {button && button[0] && (
            <Button
              {...button[0]}
              onClick={(e) => {
                onButtonClick?.();
                button[0]?.onClick?.(e);
              }}
              size="small"
              className="uipm:border-2 uipm:border-[var(--surface-surface-accent)] uipm:rounded-[var(--radius-radius-sm)]"
            />
          )}
        </div>
      </div>
      <div
        aria-hidden="true"
        className="uipm:absolute uipm:border-b uipm:border-[var(--color-color-light)] uipm:inset-0 uipm:pointer-events-none"
      />
    </div>
  );
}
